/**
 * Pure utility functions for the blocking post-login feedback dialog.
 * No React, no i18next runtime — Vitest-friendly.
 */

import {DeclineReason, OpenRequestFeedbackDTO} from "@/api/data-contracts";
import {requestDeepLinkPath} from "@/pages/login-page/expiring-hint";
import {convertToThreeDigitString} from "@/lib/utils";
import {isFreeTextMandatory} from "@/common/request-cards/decline-reason-logic";

/**
 * In-progress answer state of a single stacked feedback entry: the marker id plus the currently
 * selected reason (undefined until the user picks one), the current free-text input, and whether
 * the chosen reason may be shared with the affected dismissed bidders (opt-out, defaults to true).
 */
export interface FeedbackEntryState {
    feedbackId: number;
    reason: DeclineReason | undefined;
    freeText: string | null;
    shareWithBidder: boolean;
}

/**
 * Builds the initial (unanswered) state for a single stacked feedback entry: no reason selected yet,
 * no free text, and {@link FeedbackEntryState.shareWithBidder} defaulted to true so the reason is
 * shared with the affected dismissed bidders unless the user opts out per entry.
 *
 * @param feedbackId - the feedback marker id this entry answers
 */
export function createInitialFeedbackEntry(feedbackId: number): FeedbackEntryState {
    return {feedbackId, reason: undefined, freeText: null, shareWithBidder: true};
}

/**
 * Returns true when a single stacked entry is validly answered: a reason is selected AND (the reason
 * does not require free text OR the free text is non-blank). {@link DeclineReason.NO_REASON_GIVEN}
 * is a valid selectable reason and needs no free text.
 *
 * @param entry - the entry's in-progress answer state
 */
export function isEntryAnswered(entry: FeedbackEntryState): boolean {
    if (entry.reason === undefined) {
        return false;
    }
    if (!isFreeTextMandatory(entry.reason)) {
        return true;
    }
    return entry.freeText !== null && entry.freeText.trim().length > 0;
}

/**
 * Returns true when the stack is non-empty AND every entry is validly answered — drives the single
 * shared Confirm button, which stays disabled while any entry is still unanswered (and for an empty
 * stack, which has nothing to confirm).
 *
 * @param entries - the in-progress answer states of all stacked entries
 */
export function areAllEntriesAnswered(entries: FeedbackEntryState[]): boolean {
    return entries.length > 0 && entries.every(isEntryAnswered);
}

/**
 * Prepares the whole stack for the "close / x" path (safety-prompt confirmed): entries that already
 * have a chosen reason are returned unchanged (reason and free text preserved), while every entry
 * WITHOUT a chosen reason is defaulted to {@link DeclineReason.NO_REASON_GIVEN} with a null
 * free text. Pure and immutable — returns a new array; an empty input yields an empty output.
 *
 * @param entries - the in-progress answer states of all stacked entries
 */
export function fillUnansweredWithNoReason(entries: FeedbackEntryState[]): FeedbackEntryState[] {
    return entries.map((entry) =>
        entry.reason === undefined
            ? { ...entry, reason: DeclineReason.NO_REASON_GIVEN, freeText: null }
            : entry
    );
}

/**
 * Returns a human-readable display identifier for the oldest open feedback marker.
 * Mirrors the format used by buildExpiringRequestLabel: companyNumber (zero-padded to 3 digits) + "-" +
 * customerReferenceNumber. Fallback chain: companyNumber+customerReferenceNumber → customerReferenceNumber
 * alone → String(requestId) → "".
 *
 * @param dto - the oldest open feedback marker DTO
 */
export function buildFeedbackRequestLabel(dto: OpenRequestFeedbackDTO): string {
    const { companyNumber, customerReferenceNumber, requestId } = dto;
    if (
        companyNumber !== undefined &&
        companyNumber !== null &&
        customerReferenceNumber !== undefined &&
        customerReferenceNumber !== null
    ) {
        return `${convertToThreeDigitString(companyNumber)}-${customerReferenceNumber}`;
    }
    if (customerReferenceNumber !== undefined && customerReferenceNumber !== null) {
        return customerReferenceNumber;
    }
    if (requestId !== undefined && requestId !== null) {
        return String(requestId);
    }
    return "";
}

/**
 * Returns the deep-link URL (for opening in a NEW browser tab) to the internal-request detail page of the
 * given request id. Parameters instead of reading window.location directly, so the function stays
 * Vitest-testable. Reuses requestDeepLinkPath (path without leading "#") from expiring-hint.ts.
 *
 * @param origin - window.location.origin of the current tab
 * @param pathname - window.location.pathname of the current tab
 * @param requestId - request id to deep-link to
 */
export function buildRequestFeedbackDeepLinkUrl(origin: string, pathname: string, requestId: number): string {
    return `${origin}${pathname}#${requestDeepLinkPath(requestId)}`;
}
